import { useField } from "formik";
import { useState } from "react";
import { Check } from "lucide-react";
import CustomInput from "./Input";

/**
 * Password creation field — a kit `<CustomInput type="password">` plus a
 * live requirements popover (strength bar + checklist) that appears below
 * the field on focus. Guidance is shown as positive ✓/✗ feedback while the
 * user types, not as a red error; the inline error only surfaces on blur
 * (handled by the field's `touched` state). The popover is absolutely
 * positioned so it never shifts the form layout.
 *
 * Rules mirror the auth Yup schema: 8+ chars, one upper, one lower, one
 * special character.
 */

interface PasswordRule {
  key: string;
  label: string;
  test: (pw: string) => boolean;
}

const RULES: PasswordRule[] = [
  { key: "length", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  {
    key: "special",
    label: "One special character",
    test: (p) => /[^a-zA-Z0-9]/.test(p),
  },
];

// Standard strength-meter palette (red → amber → green), independent of the
// brand accent so the level reads unambiguously.
const WEAK = "#DE1E5D";
const MEDIUM = "#C8861A";
const STRONG = "#2F8F5E";

function getLevel(metCount: number): { label: string; color: string } | null {
  if (metCount === 0) return null;
  if (metCount <= 2) return { label: "Weak", color: WEAK };
  if (metCount === 3) return { label: "Medium", color: MEDIUM };
  return { label: "Strong", color: STRONG };
}

function PasswordRequirements({ password }: { password: string }) {
  const results = RULES.map((r) => ({ ...r, met: r.test(password) }));
  const metCount = results.filter((r) => r.met).length;
  const level = getLevel(metCount);

  return (
    <div className="relative rounded-md border border-[var(--d-rule)] bg-[var(--d-surface)] shadow-[0_8px_24px_-12px_rgba(28,26,22,0.25)] p-3">
      {/* Strength meter — four segments fill with the current level colour. */}
      <div className="flex items-center gap-2 mb-2.5">
        <div className="flex-1 flex gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex-1 h-1.5 rounded-full transition-colors"
              style={{
                background:
                  i < metCount && level ? level.color : "var(--d-rule)",
              }}
            />
          ))}
        </div>
        {level && (
          <span
            className="text-[11px] font-medium shrink-0"
            style={{ color: level.color }}
          >
            {level.label}
          </span>
        )}
      </div>

      {/* Live checklist. */}
      <ul className="flex flex-col gap-1">
        {results.map((r) => (
          <li
            key={r.key}
            className="flex items-center gap-1.5 text-[12px] leading-[1.4] transition-colors"
            style={{ color: r.met ? STRONG : "var(--d-ink-muted)" }}
          >
            <span className="inline-flex w-3.5 h-3.5 items-center justify-center shrink-0">
              {r.met ? (
                <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--d-rule)]" />
              )}
            </span>
            {r.label}
          </li>
        ))}
      </ul>

      {/* Rounded speech-bubble tail at the bottom-left, pointing down to the
          input. A 45deg rotated square whose lower-right faces carry the panel
          border; the surface fill hides the panel's bottom edge behind it and
          the rounded corner softens the tip. */}
      <span
        aria-hidden="true"
        className="absolute right-5 bottom-[-9px] w-[18px] h-[18px] rotate-45 rounded-br-[4px]"
        style={{
          background: "var(--d-surface)",
          borderRight: "1px solid var(--d-rule)",
          borderBottom: "1px solid var(--d-rule)",
        }}
      />
    </div>
  );
}

interface PasswordFieldProps {
  name: string;
  label: string;
  placeholder?: string;
}

export function PasswordField({ name, label, placeholder }: PasswordFieldProps) {
  const [field] = useField<string>(name);
  const [focused, setFocused] = useState(false);

  // No inline error message here on purpose: the requirements popover (strength
  // bar + live checklist) is the guidance, and the submit button stays disabled
  // until the form is valid. A red "Password must contain…" while typing would
  // just duplicate the checklist.
  return (
    <div className="relative">
      <CustomInput
        type="password"
        id={name}
        name={name}
        label={label}
        placeholder={placeholder}
        value={field.value ?? ""}
        onChange={field.onChange}
        onBlur={(e) => {
          field.onBlur(e);
          setFocused(false);
        }}
        onFocus={() => setFocused(true)}
      />
      {focused && (
        <div className="absolute left-0 right-0 bottom-full z-20 mb-1">
          <PasswordRequirements password={field.value ?? ""} />
        </div>
      )}
    </div>
  );
}
